import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

// Convert 24-hour format to 12-hour format
function formatTime12h(time24) {
  if (!time24) return '';
  
  // Handle range format like "13:00 - 15:30"
  if (time24.includes(' - ')) {
    const [start, end] = time24.split(' - ');
    return `${formatSingleTime(start)} - ${formatSingleTime(end)}`;
  }
  
  return formatSingleTime(time24);
}

function formatSingleTime(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time24;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function AdminInvoices({ token }) {
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [view, setView] = useState('bookings'); // 'bookings', 'invoices', 'detail', 'payment-terms'
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [timezone, setTimezone] = useState('America/New_York');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [invoiceFilter, setInvoiceFilter] = useState('all'); // all, invoiced, not-invoiced
  const [invoiceDateFilter, setInvoiceDateFilter] = useState('issued'); // issued or due
  
  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedUserInfo, setExpandedUserInfo] = useState(new Set());
  
  // Invoice generation
  const [taxRate, setTaxRate] = useState('0');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  
  // Payment modal state
  const [paymentModal, setPaymentModal] = useState({ open: false, invoiceId: null });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentNotes, setPaymentNotes] = useState('');
  
  // Notes editing state
  const [editingAdminNotes, setEditingAdminNotes] = useState(false);
  const [editingCustomerNotes, setEditingCustomerNotes] = useState(false);
  const [editingLineNote, setEditingLineNote] = useState(null);
  const [tempAdminNotes, setTempAdminNotes] = useState('');
  const [tempCustomerNotes, setTempCustomerNotes] = useState('');
  const [tempLineNote, setTempLineNote] = useState('');
  
  // Due date editing state
  const [editingDueDate, setEditingDueDate] = useState(false);
  const [tempDueDate, setTempDueDate] = useState('');
  
  // Apply terms modal state
  const [applyTermsModal, setApplyTermsModal] = useState(false);
  const [selectedTermId, setSelectedTermId] = useState('');
  
  // Payment terms state
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [editingTerm, setEditingTerm] = useState(null);
  const [termForm, setTermForm] = useState({
    name: '',
    description: '',
    min_amount: '',
    is_default: false,
    installments: [{ percentage: 100, due_type: 'upon_receipt', days_before: '', is_refundable: true }]
  });

  // Custom invoice modal state
  const [customInvoiceModal, setCustomInvoiceModal] = useState(false);
  const [customInvoiceUserId, setCustomInvoiceUserId] = useState('');
  const [customInvoiceItems, setCustomInvoiceItems] = useState([{ description: '', quantity: 1, unit_price: '' }]);
  const [customInvoiceTaxRate, setCustomInvoiceTaxRate] = useState('0');
  const [customInvoiceNotes, setCustomInvoiceNotes] = useState('');
  const [customInvoiceDueDate, setCustomInvoiceDueDate] = useState('');
  const [customers, setCustomers] = useState([]);

  const toggleUserInfo = (bookingId) => {
    setExpandedUserInfo(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bookingId)) {
        newSet.delete(bookingId);
      } else {
        newSet.add(bookingId);
      }
      return newSet;
    });
  };

  const fetchBookings = useCallback(async () => {
    try {
      // Use grouped mode for invoicing - bookings made together should be invoiced together
      const res = await fetch(`${API_URL}/api/admin/bookings?grouped=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      setBookings([]);
    }
  }, [token]);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setInvoices(Array.isArray(data) ? data : []);
    } catch {
      setInvoices([]);
    }
  }, [token]);

  const fetchPaymentTerms = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/payment-terms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPaymentTerms(Array.isArray(data) ? data : []);
    } catch {
      setPaymentTerms([]);
    }
  }, [token]);

  const fetchInvoiceDetail = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setSelectedInvoice(data);
        setView('detail');
      } else {
        setError(data.error || 'Failed to load invoice');
      }
    } catch {
      setError('Failed to load invoice');
    }
  };

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data.filter(u => !u.is_admin) : []);
    } catch {
      setCustomers([]);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
    fetchInvoices();
    fetchPaymentTerms();
    fetchCustomers();
    // Fetch timezone setting
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, [fetchBookings, fetchInvoices, fetchPaymentTerms, fetchCustomers]);

  // Clear selections when filters change
  useEffect(() => {
    setSelectedIds([]);
  }, [searchTerm, statusFilter, invoiceFilter, dateFrom, dateTo]);

  const handleGenerateInvoice = async () => {
    setMessage('');
    setError('');

    if (selectedCount === 0) {
      setError('Please select at least one booking');
      return;
    }

    // Get selected bookings from filtered list only
    const selectedBookings = filteredBookings.filter(b => selectedIds.includes(b.id));
    
    // Check all are from same user
    const userIds = [...new Set(selectedBookings.map(b => b.user_id))];
    if (userIds.length > 1) {
      setError('All selected bookings must be from the same customer');
      return;
    }

    // Check all are approved or pending
    const invalidStatus = selectedBookings.filter(b => b.status !== 'approved' && b.status !== 'pending');
    if (invalidStatus.length > 0) {
      setError('Only approved or pending bookings can be invoiced');
      return;
    }

    // Check none are already invoiced
    const alreadyInvoiced = selectedBookings.filter(b => b.invoice_id);
    if (alreadyInvoiced.length > 0) {
      setError('Some selected bookings are already invoiced');
      return;
    }

    // Flatten all booking IDs from grouped bookings (each grouped booking has booking_ids array)
    const allBookingIds = selectedBookings.flatMap(b => b.booking_ids || [b.id]);
    console.log('Selected bookings:', selectedBookings);
    console.log('All booking IDs to invoice:', allBookingIds);

    try {
      const res = await fetch(`${API_URL}/api/invoices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userIds[0],
          booking_ids: allBookingIds,
          tax_rate: parseFloat(taxRate) / 100,
          notes,
          due_date: dueDate || null
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create invoice');

      setMessage(`Invoice ${data.invoice_number} created successfully!`);
      setSelectedIds([]);
      setTaxRate('0');
      setNotes('');
      setDueDate('');
      fetchBookings();
      fetchInvoices();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateCustomInvoice = async () => {
    setMessage('');
    setError('');

    if (!customInvoiceUserId) {
      setError('Please select a customer');
      return;
    }

    const validItems = customInvoiceItems.filter(item => 
      item.description.trim() && item.quantity > 0 && parseFloat(item.unit_price) > 0
    );

    if (validItems.length === 0) {
      setError('Please add at least one item with description, quantity, and price');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/invoices/custom`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: parseInt(customInvoiceUserId),
          items: validItems.map(item => ({
            description: item.description.trim(),
            quantity: parseFloat(item.quantity),
            unit_price: parseFloat(item.unit_price)
          })),
          tax_rate: parseFloat(customInvoiceTaxRate) / 100,
          notes: customInvoiceNotes || null,
          due_date: customInvoiceDueDate || null
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create custom invoice');

      setMessage(`Custom Invoice ${data.invoice_number} created successfully!`);
      setCustomInvoiceModal(false);
      setCustomInvoiceUserId('');
      setCustomInvoiceItems([{ description: '', quantity: 1, unit_price: '' }]);
      setCustomInvoiceTaxRate('0');
      setCustomInvoiceNotes('');
      setCustomInvoiceDueDate('');
      fetchInvoices();
    } catch (err) {
      setError(err.message);
    }
  };

  const addCustomInvoiceItem = () => {
    setCustomInvoiceItems([...customInvoiceItems, { description: '', quantity: 1, unit_price: '' }]);
  };

  const removeCustomInvoiceItem = (index) => {
    if (customInvoiceItems.length > 1) {
      setCustomInvoiceItems(customInvoiceItems.filter((_, i) => i !== index));
    }
  };

  const updateCustomInvoiceItem = (index, field, value) => {
    const updated = [...customInvoiceItems];
    updated[index][field] = value;
    setCustomInvoiceItems(updated);
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update status');
      }

      setMessage('Invoice status updated');
      fetchInvoices();
      if (selectedInvoice && selectedInvoice.id === id) {
        fetchInvoiceDetail(id);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendInvoice = async (id) => {
    setMessage('');
    setError('');
    
    if (!window.confirm('Send this invoice via email to the customer?')) {
      return;
    }
    
    try {
      setMessage('Sending invoice...');
      const res = await fetch(`${API_URL}/api/invoices/${id}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send invoice');
      }

      setMessage('Invoice sent successfully!');
      fetchInvoices();
      if (selectedInvoice && selectedInvoice.id === id) {
        fetchInvoiceDetail(id);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMarkAsSent = async (id) => {
    setMessage('');
    setError('');
    
    if (!window.confirm('Mark this invoice as sent without emailing?')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/invoices/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'sent' })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update invoice status');
      }

      setMessage('Invoice marked as sent!');
      fetchInvoices();
      if (selectedInvoice && selectedInvoice.id === id) {
        fetchInvoiceDetail(id);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRecordPayment = async () => {
    setMessage('');
    setError('');

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/invoices/${paymentModal.invoiceId}/payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(paymentAmount),
          payment_method: paymentMethod,
          reference_number: paymentRef,
          payment_date: paymentDate,
          notes: paymentNotes
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to record payment');

      setMessage('Payment recorded successfully');
      setPaymentModal({ open: false, invoiceId: null });
      setPaymentAmount('');
      setPaymentMethod('');
      setPaymentRef('');
      setPaymentDate(new Date().toISOString().slice(0, 10));
      setPaymentNotes('');
      fetchInvoices();
      if (selectedInvoice) {
        fetchInvoiceDetail(selectedInvoice.id);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to generate PDF');
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this invoice? This will also remove all associated payments and unlink any bookings.')) return;

    try {
      const res = await fetch(`${API_URL}/api/invoices/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete invoice');
      }

      setMessage('Invoice deleted');
      setView('invoices');
      setSelectedInvoice(null);
      fetchInvoices();
      fetchBookings();
    } catch (err) {
      setError(err.message);
    }
  };

  // Save admin notes (internal only)
  const handleSaveAdminNotes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/notes`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ admin_notes: tempAdminNotes })
      });
      
      if (!res.ok) throw new Error('Failed to save admin notes');
      
      setSelectedInvoice(prev => ({ ...prev, admin_notes: tempAdminNotes }));
      setEditingAdminNotes(false);
      setMessage('Admin notes saved');
    } catch (err) {
      setError(err.message);
    }
  };

  // Save due date
  const handleSaveDueDate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/due-date`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ due_date: tempDueDate })
      });
      
      if (!res.ok) throw new Error('Failed to save due date');
      
      setSelectedInvoice(prev => ({ ...prev, due_date: tempDueDate }));
      setEditingDueDate(false);
      setMessage('Due date updated');
      fetchInvoices(); // Refresh invoices list
    } catch (err) {
      setError(err.message);
    }
  };

  // Apply payment terms to invoice
  const handleApplyTerms = async () => {
    setMessage('');
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/apply-terms`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ payment_terms_id: selectedTermId || null })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to apply payment terms');
      
      // Refresh the invoice to get updated installments
      await fetchInvoiceDetail(selectedInvoice.id);
      setApplyTermsModal(false);
      setSelectedTermId('');
      setMessage(data.message || 'Payment terms applied');
      fetchInvoices();
    } catch (err) {
      setError(err.message);
    }
  };

  // Save customer notes (visible on invoice)
  const handleSaveCustomerNotes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/notes`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: tempCustomerNotes })
      });
      
      if (!res.ok) throw new Error('Failed to save customer notes');
      
      setSelectedInvoice(prev => ({ ...prev, notes: tempCustomerNotes }));
      setEditingCustomerNotes(false);
      setMessage('Customer notes saved');
    } catch (err) {
      setError(err.message);
    }
  };

  // Save line item note
  const handleSaveLineNote = async (itemId) => {
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/items/${itemId}/note`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ line_note: tempLineNote })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save line note');
      
      // Update the item in selectedInvoice
      setSelectedInvoice(prev => ({
        ...prev,
        items: prev.items.map(item => 
          item.id === itemId ? { ...item, line_note: tempLineNote } : item
        )
      }));
      setEditingLineNote(null);
      setTempLineNote('');
      setMessage('Line note saved');
    } catch (err) {
      setError(err.message);
    }
  };

  // Payment Terms Management
  const resetTermForm = () => {
    setTermForm({
      name: '',
      description: '',
      min_amount: '',
      is_default: false,
      installments: [{ percentage: 100, due_type: 'upon_receipt', days_before: '', is_refundable: true }]
    });
    setEditingTerm(null);
  };

  const handleSavePaymentTerm = async () => {
    setMessage('');
    setError('');
    
    if (!termForm.name.trim()) {
      setError('Term name is required');
      return;
    }
    
    const totalPercent = termForm.installments.reduce((sum, i) => sum + (parseFloat(i.percentage) || 0), 0);
    if (Math.abs(totalPercent - 100) > 0.01) {
      setError(`Installment percentages must total 100% (currently ${totalPercent}%)`);
      return;
    }
    
    try {
      const method = editingTerm ? 'PUT' : 'POST';
      const url = editingTerm 
        ? `${API_URL}/api/payment-terms/${editingTerm.id}`
        : `${API_URL}/api/payment-terms`;
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: termForm.name,
          description: termForm.description,
          min_amount: parseFloat(termForm.min_amount) || 0,
          is_default: termForm.is_default,
          installments: termForm.installments
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save payment term');
      }
      
      setMessage(editingTerm ? 'Payment term updated' : 'Payment term created');
      resetTermForm();
      fetchPaymentTerms();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditTerm = (term) => {
    setTermForm({
      name: term.name,
      description: term.description || '',
      min_amount: term.min_amount || '',
      is_default: term.is_default,
      installments: term.installments || [{ percentage: 100, due_type: 'upon_receipt', days_before: '', is_refundable: true }]
    });
    setEditingTerm(term);
  };

  const handleDeleteTerm = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment term?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/payment-terms/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to delete payment term');
      
      setMessage('Payment term deleted');
      fetchPaymentTerms();
    } catch (err) {
      setError(err.message);
    }
  };

  const addInstallment = () => {
    setTermForm(prev => ({
      ...prev,
      installments: [...prev.installments, { percentage: 0, due_type: 'days_before', days_before: 30, is_refundable: true }]
    }));
  };

  const removeInstallment = (index) => {
    if (termForm.installments.length === 1) return;
    setTermForm(prev => ({
      ...prev,
      installments: prev.installments.filter((_, i) => i !== index)
    }));
  };

  const updateInstallment = (index, field, value) => {
    setTermForm(prev => ({
      ...prev,
      installments: prev.installments.map((inst, i) => 
        i === index ? { ...inst, [field]: value } : inst
      )
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const datePart = dateStr.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { timeZone: timezone });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatBookingType = (type) => {
    if (!type) return '-';
    if (type === 'full_turf') return 'Full Turf';
    if (type === 'half_turf') return 'Half Turf';
    if (type === 'cage_rental') return 'Cage';
    return type.replace('_', ' ');
  };

  // Export invoices to CSV with payment details
  const exportInvoicesCSV = async () => {
    setMessage('Preparing export...');
    
    // Build headers with payment columns (up to 10 payments)
    const baseHeaders = [
      'Invoice #',
      'Customer Name',
      'Email',
      'Phone',
      'Organization',
      'Issue Date',
      'Due Date',
      'Subtotal',
      'Tax',
      'Total',
      'Amount Paid',
      'Balance Due',
      'Status'
    ];
    
    // Add payment columns (10 payments max)
    const paymentHeaders = [];
    for (let i = 1; i <= 10; i++) {
      paymentHeaders.push(`Payment ${i} Date`);
      paymentHeaders.push(`Payment ${i} Amount`);
      paymentHeaders.push(`Payment ${i} Method`);
      paymentHeaders.push(`Payment ${i} Reference`);
    }
    
    const headers = [...baseHeaders, ...paymentHeaders];
    
    // Escape CSV values
    const escapeCSV = (val) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    
    try {
      // Fetch payment details for each invoice
      const rows = await Promise.all(filteredInvoices.map(async (inv) => {
        // Fetch payments for this invoice
        let payments = [];
        try {
          const res = await fetch(`${API_URL}/api/invoices/${inv.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            payments = data.payments || [];
          }
        } catch (e) {
          console.error('Failed to fetch payments for invoice', inv.id);
        }
        
        const baseRow = [
          inv.invoice_number,
          `${inv.first_name || ''} ${inv.last_name || ''}`.trim(),
          inv.email || '',
          inv.phone || '',
          inv.organization || '',
          inv.issued_date ? inv.issued_date.substring(0, 10) : '',
          inv.due_date ? inv.due_date.substring(0, 10) : '',
          inv.subtotal || 0,
          inv.tax || 0,
          inv.total || 0,
          inv.amount_paid || 0,
          inv.balance_due || 0,
          inv.status || ''
        ];
        
        // Add payment columns (up to 10)
        const paymentCols = [];
        for (let i = 0; i < 10; i++) {
          if (payments[i]) {
            paymentCols.push(payments[i].payment_date ? payments[i].payment_date.substring(0, 10) : '');
            paymentCols.push(payments[i].amount || '');
            paymentCols.push(payments[i].payment_method || '');
            paymentCols.push(payments[i].reference_number || '');
          } else {
            paymentCols.push('', '', '', '');
          }
        }
        
        return [...baseRow, ...paymentCols];
      }));
      
      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const today = new Date().toISOString().substring(0, 10);
      link.setAttribute('download', `invoices-export-${today}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setMessage('Export complete!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('Failed to export invoices');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: '#2563eb',
      approved: '#22c55e',
      rejected: '#ef4444',
      cancelled: '#9ca3af',
      sent: '#2563eb',
      paid: '#22c55e',
      partial: '#f59e0b',
      overdue: '#ef4444'
    };
    return (
      <span style={{
        background: colors[status] || '#9ca3af',
        color: '#fff',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.85rem',
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  // Filter bookings
  const filteredBookings = bookings
    .filter(b => {
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (invoiceFilter === 'invoiced' && !b.invoice_id) return false;
      if (invoiceFilter === 'not-invoiced' && b.invoice_id) return false;
      const bookingDateOnly = b.booking_date ? b.booking_date.substring(0, 10) : '';
      if (dateFrom && bookingDateOnly < dateFrom) return false;
      if (dateTo && bookingDateOnly > dateTo) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const fullName = `${b.first_name || ''} ${b.last_name || ''}`.trim();
        const searchFields = [
          fullName, b.first_name, b.last_name, b.username, b.email, b.phone, b.organization
        ].filter(Boolean).map(f => f.toLowerCase());
        if (!searchFields.some(f => f.includes(term))) return false;
      }
      return true;
    })
    .sort((a, b) => new Date(b.booking_date) - new Date(a.booking_date));

  // Filter invoices
  const filteredInvoices = invoices
    .filter(inv => {
      // Date filter based on selected date type (compare date portion only)
      const dateField = invoiceDateFilter === 'due' ? inv.due_date : inv.issued_date;
      const dateOnly = dateField ? dateField.substring(0, 10) : '';
      if (dateFrom && dateOnly < dateFrom) return false;
      if (dateTo && dateOnly > dateTo) return false;
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const fullName = `${inv.first_name || ''} ${inv.last_name || ''}`.trim();
        const searchFields = [
          fullName, inv.first_name, inv.last_name, inv.email, inv.phone, inv.organization, inv.invoice_number
        ].filter(Boolean).map(f => f.toLowerCase());
        if (!searchFields.some(f => f.includes(term))) return false;
      }
      return true;
    })
    .sort((a, b) => new Date(b.issued_date) - new Date(a.issued_date));

  const toggleSelectAll = () => {
    const selectableBookings = filteredBookings.filter(b => (b.status === 'approved' || b.status === 'pending') && !b.invoice_id);
    if (selectedIds.length === selectableBookings.length && selectableBookings.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableBookings.map(b => b.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Calculate totals for selected bookings (only from currently filtered view)
  const filteredSelectedBookings = filteredBookings.filter(b => selectedIds.includes(b.id));
  const selectedTotal = filteredSelectedBookings.reduce((sum, b) => sum + (b.price || 0), 0);
  const selectedCount = filteredSelectedBookings.length;

  return (
    <div className="admin-invoices">
      <div className="invoices-header">
        <h2>Invoices</h2>
      </div>
      
      {/* Search and Filter Row */}
      <div className="invoice-search-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, phone, org..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="date-filters">
          {view === 'invoices' && (
            <select 
              value={invoiceDateFilter} 
              onChange={e => setInvoiceDateFilter(e.target.value)}
              className="date-type-select"
            >
              <option value="issued">Created Date</option>
              <option value="due">Due Date</option>
            </select>
          )}
          <label>
            From:
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </label>
          <label>
            To:
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </label>
          {view === 'invoices' && (
            <button className="export-csv-btn" onClick={exportInvoicesCSV}>
              📥 Export CSV
            </button>
          )}
        </div>
      </div>
      
      {/* View Tabs */}
      <div className="invoice-view-tabs">
        <button 
          className={`invoice-tab ${view === 'bookings' ? 'active' : ''}`}
          onClick={() => setView('bookings')}
        >
          Bookings
        </button>
        <button 
          className={`invoice-tab ${view === 'invoices' ? 'active' : ''}`}
          onClick={() => setView('invoices')}
        >
          Invoices ({invoices.length})
        </button>
        <button 
          className={`invoice-tab ${view === 'payment-terms' ? 'active' : ''}`}
          onClick={() => setView('payment-terms')}
        >
          Payment Terms
        </button>
        <button 
          className="custom-invoice-btn"
          onClick={() => setCustomInvoiceModal(true)}
          style={{ marginLeft: 'auto', background: '#059669', color: 'white', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
        >
          + Custom Invoice
        </button>
      </div>

      {message && <div className="success">{message}</div>}
      {error && <div className="error">{error}</div>}

      {/* Custom Invoice Modal */}
      {customInvoiceModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setCustomInvoiceModal(false)}
        >
          <div 
            onClick={e => e.stopPropagation()} 
            style={{ 
              maxWidth: '600px', 
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
            }}
          >
            <h3 style={{ marginTop: 0 }}>Create Custom Invoice</h3>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>For miscellaneous services, equipment rentals, etc.</p>
            
            <div className="form-group">
              <label>Customer *</label>
              <select 
                value={customInvoiceUserId} 
                onChange={e => setCustomInvoiceUserId(e.target.value)}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              >
                <option value="">Select a customer...</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name} ({c.email}){c.organization ? ` - ${c.organization}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginTop: '16px' }}>
              <label>Line Items *</label>
              {customInvoiceItems.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Description (e.g., Equipment rental)"
                    value={item.description}
                    onChange={e => updateCustomInvoiceItem(index, 'description', e.target.value)}
                    style={{ flex: 2, padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    min="1"
                    step="0.5"
                    value={item.quantity}
                    onChange={e => updateCustomInvoiceItem(index, 'quantity', e.target.value)}
                    style={{ width: '70px', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                  <input
                    type="number"
                    placeholder="Unit Price"
                    min="0"
                    step="0.01"
                    value={item.unit_price}
                    onChange={e => updateCustomInvoiceItem(index, 'unit_price', e.target.value)}
                    style={{ width: '100px', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                  <span style={{ width: '80px', textAlign: 'right' }}>
                    ${((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                  </span>
                  {customInvoiceItems.length > 1 && (
                    <button 
                      onClick={() => removeCustomInvoiceItem(index)}
                      style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button 
                onClick={addCustomInvoiceItem}
                style={{ background: '#e5e7eb', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', marginTop: '4px' }}
              >
                + Add Item
              </button>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Tax Rate (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={customInvoiceTaxRate}
                  onChange={e => setCustomInvoiceTaxRate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Due Date</label>
                <input
                  type="date"
                  value={customInvoiceDueDate}
                  onChange={e => setCustomInvoiceDueDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '16px' }}>
              <label>Notes</label>
              <textarea
                value={customInvoiceNotes}
                onChange={e => setCustomInvoiceNotes(e.target.value)}
                placeholder="Optional notes for the customer..."
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', minHeight: '60px' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                Subtotal: ${customInvoiceItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0).toFixed(2)}
                {parseFloat(customInvoiceTaxRate) > 0 && (
                  <span style={{ color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
                    + ${(customInvoiceItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0) * parseFloat(customInvoiceTaxRate) / 100).toFixed(2)} tax
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={() => setCustomInvoiceModal(false)}
                  style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleCreateCustomInvoice}
                  style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', background: '#059669', color: 'white', cursor: 'pointer' }}
                >
                  Create Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bookings View */}
      {view === 'bookings' && (
        <>
          {/* Generate Invoice Section */}
          {selectedCount > 0 && (
            <div className="generate-invoice-section">
              <h4>Generate Invoice for {selectedCount} booking(s) - Total: {formatCurrency(selectedTotal)}</h4>
              <div className="invoice-options">
                <label>
                  Tax Rate (%)
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={taxRate}
                    onChange={e => setTaxRate(e.target.value)}
                    style={{ width: '80px' }}
                  />
                </label>
                <label>
                  Due Date
                  <input
                    type="date"
                    value={dueDate}
                    onChange={e => setDueDate(e.target.value)}
                  />
                </label>
                <label>
                  Notes
                  <input
                    type="text"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Invoice notes..."
                    style={{ width: '200px' }}
                  />
                </label>
                <button className="generate-btn" onClick={handleGenerateInvoice}>
                  Generate Invoice
                </button>
              </div>
            </div>
          )}

          {/* Status/Invoice Filters */}
          <div className="booking-status-filters">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select value={invoiceFilter} onChange={e => setInvoiceFilter(e.target.value)}>
              <option value="all">All Bookings</option>
              <option value="not-invoiced">Not Invoiced</option>
              <option value="invoiced">Invoiced</option>
            </select>
          </div>

          {/* Bookings Table */}
          {filteredBookings.length === 0 ? (
            <p className="no-invoices">No bookings found.</p>
          ) : (
            <div className="table-scroll-wrapper">
            <table className="admin-bookings-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredBookings.filter(b => (b.status === 'approved' || b.status === 'pending') && !b.invoice_id).length && filteredBookings.filter(b => (b.status === 'approved' || b.status === 'pending') && !b.invoice_id).length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>User</th>
                  <th>Time Slot</th>
                  <th>Type</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map(b => (
                  <tr key={b.id} className={selectedIds.includes(b.id) ? 'selected' : ''}>
                    <td>
                      {(b.status === 'approved' || b.status === 'pending') && !b.invoice_id && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(b.id)}
                          onChange={() => toggleSelect(b.id)}
                        />
                      )}
                    </td>
                    <td>
                      <div className="user-info-cell">
                        <span className="user-name">
                          {b.first_name} {b.last_name}
                        </span>
                        {(b.email || b.phone || b.organization) && (
                          <button 
                            className="more-info-btn"
                            onClick={() => toggleUserInfo(b.id)}
                          >
                            {expandedUserInfo.has(b.id) ? 'hide' : 'more info'}
                          </button>
                        )}
                        {expandedUserInfo.has(b.id) && (
                          <div className="user-info-expanded">
                            {b.organization && <small><strong>{b.organization}</strong></small>}
                            {b.email && <small>{b.email}</small>}
                            {b.phone && <small>{b.phone}</small>}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="time-slot-cell">
                      <div className="date-line">{formatDate(b.booking_date)}</div>
                      <div className="time-line">{formatTime12h(b.time_slot)}</div>
                      {b.slot_count > 1 && (
                        <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                          ({b.slot_count} slots)
                        </small>
                      )}
                    </td>
                    <td>{formatBookingType(b.booking_type)}</td>
                    <td>{b.price ? formatCurrency(b.price) : '-'}</td>
                    <td>{getStatusBadge(b.status)}</td>
                    <td>
                      {b.invoice_number ? (
                        <button 
                          className="link-btn"
                          onClick={() => {
                            const inv = invoices.find(i => i.invoice_number === b.invoice_number);
                            if (inv) fetchInvoiceDetail(inv.id);
                          }}
                        >
                          {b.invoice_number}
                        </button>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}

      {/* Invoices List View */}
      {view === 'invoices' && (
        <div className="invoices-list">
          {filteredInvoices.length === 0 ? (
            <p className="no-invoices">{invoices.length === 0 ? 'No invoices found.' : 'No invoices match your search criteria.'}</p>
          ) : (
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th>Total</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(inv => (
                  <tr key={inv.id}>
                    <td>
                      <button className="link-btn" onClick={() => fetchInvoiceDetail(inv.id)}>
                        {inv.invoice_number}
                      </button>
                    </td>
                    <td>
                      <div className="user-info-cell">
                        <div className="user-name">
                          {inv.first_name && inv.last_name 
                            ? `${inv.first_name} ${inv.last_name}`
                            : '-'}
                        </div>
                        {(inv.email || inv.phone || inv.organization) && (
                          <button 
                            className="more-info-btn" 
                            onClick={() => toggleUserInfo(`inv-${inv.id}`)}
                          >
                            {expandedUserInfo.has(`inv-${inv.id}`) ? 'hide' : 'more info'}
                          </button>
                        )}
                        {expandedUserInfo.has(`inv-${inv.id}`) && (
                          <div className="user-info-expanded">
                            {inv.organization && <small><strong>{inv.organization}</strong></small>}
                            {inv.email && <small>{inv.email}</small>}
                            {inv.phone && <small>{inv.phone}</small>}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{formatDate(inv.issued_date)}</td>
                    <td>{formatDate(inv.due_date)}</td>
                    <td>{formatCurrency(inv.total)}</td>
                    <td>{formatCurrency(inv.amount_paid)}</td>
                    <td>
                      <div className={`balance-cell ${parseFloat(inv.balance_due) > 0 ? 'balance-due' : ''}`}>
                        {formatCurrency(inv.balance_due)}
                      </div>
                      <div className="status-below-balance">
                        {getStatusBadge(inv.status)}
                      </div>
                    </td>
                    <td className="actions-cell">
                      <div className="stacked-actions">
                        {inv.status === 'pending' && (
                          <button className="action-btn resend-btn" onClick={() => handleSendInvoice(inv.id)}>
                            📧 Send
                          </button>
                        )}
                        {['sent', 'partial', 'overdue', 'paid'].includes(inv.status) && (
                          <>
                            <button className="action-btn resend-btn" onClick={() => handleSendInvoice(inv.id)}>
                              📧 Resend
                            </button>
                            {inv.status !== 'paid' && (
                              <button className="action-btn payment-btn" onClick={() => setPaymentModal({ open: true, invoiceId: inv.id })}>
                                Payment
                              </button>
                            )}
                          </>
                        )}
                        {inv.status === 'pending' && inv.status !== 'paid' && (
                          <button className="action-btn payment-btn" onClick={() => setPaymentModal({ open: true, invoiceId: inv.id })}>
                            Payment
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Invoice Detail View */}
      {view === 'detail' && selectedInvoice && (
        <div className="invoice-detail">
          <div className="detail-top-actions">
            <button className="back-btn" onClick={() => { setView('invoices'); setSelectedInvoice(null); }}>
              ← Back to Invoices
            </button>
            <button className="download-btn" onClick={() => handleDownloadPdf(selectedInvoice.id)}>
              📄 Download PDF
            </button>
          </div>
          
          <div className="invoice-header-info">
            <div>
              <h3>{selectedInvoice.invoice_number}</h3>
              <p>Issued: {formatDate(selectedInvoice.issued_date)}</p>
              
              {/* Payment Terms / Due Date Section */}
              <div className="payment-terms-section-detail" style={{ marginTop: '16px' }}>
                {selectedInvoice.payment_terms_name ? (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    padding: '8px 12px',
                    background: '#f0fdf4',
                    borderRadius: '6px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <span style={{ fontSize: '0.85rem', color: '#166534' }}>
                      <strong>Payment Terms:</strong> {selectedInvoice.payment_terms_name}
                    </span>
                    <button 
                      className="edit-btn-small" 
                      onClick={() => {
                        setSelectedTermId(selectedInvoice.payment_terms_id || '');
                        setApplyTermsModal(true);
                      }}
                      style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="due-date-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {editingDueDate ? (
                      <>
                        <label>Due: </label>
                        <input 
                          type="date" 
                          value={tempDueDate} 
                          onChange={e => setTempDueDate(e.target.value)}
                          style={{ marginRight: '8px' }}
                        />
                        <button className="save-btn-small" onClick={handleSaveDueDate}>Save</button>
                        <button className="cancel-btn-small" onClick={() => setEditingDueDate(false)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontWeight: '500' }}>Due: {selectedInvoice.due_date ? (
                          selectedInvoice.due_date.slice(0,10) === selectedInvoice.issued_date?.slice(0,10) 
                            ? 'Upon Receipt' 
                            : formatDate(selectedInvoice.due_date)
                        ) : 'Not set'}</span>
                        <button 
                          className="edit-btn-small" 
                          onClick={() => {
                            setTempDueDate(selectedInvoice.due_date?.slice(0, 10) || '');
                            setEditingDueDate(true);
                          }}
                          style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => {
                            setSelectedTermId('');
                            setApplyTermsModal(true);
                          }}
                          style={{ 
                            padding: '4px 12px', 
                            fontSize: '0.75rem',
                            background: '#0284c7',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          + Apply Terms
                        </button>
                      </>
                    )}
                  </div>
                )}
                
                {/* Show installments if payment terms applied */}
                {selectedInvoice.installments && selectedInvoice.installments.length > 0 && (
                  <div style={{ 
                    marginTop: '12px', 
                    background: '#f8fafc', 
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      padding: '8px 12px', 
                      background: '#e2e8f0', 
                      fontWeight: '600', 
                      fontSize: '0.8rem',
                      color: '#475569'
                    }}>
                      Payment Schedule
                    </div>
                    {selectedInvoice.installments.map((inst, i) => (
                      <div key={i} style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr auto auto',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        borderBottom: i < selectedInvoice.installments.length - 1 ? '1px solid #e2e8f0' : 'none',
                        fontSize: '0.85rem'
                      }}>
                        <span style={{ color: '#374151' }}>
                          <strong>{inst.installment_number}.</strong> {inst.percentage}%
                          {inst.is_refundable === false && (
                            <span style={{ 
                              marginLeft: '6px',
                              padding: '2px 6px',
                              background: '#fef2f2',
                              color: '#dc2626',
                              borderRadius: '3px',
                              fontSize: '0.7rem'
                            }}>non-refundable</span>
                          )}
                        </span>
                        <span style={{ fontWeight: '600', color: '#059669' }}>
                          {formatCurrency(inst.amount)}
                        </span>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                          Due {inst.due_date ? formatDate(inst.due_date) : 'TBD'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="invoice-status-section">
              {getStatusBadge(selectedInvoice.status)}
              {selectedInvoice.status === 'pending' && (
                <div className="status-actions">
                  <button className="resend-btn" onClick={() => handleSendInvoice(selectedInvoice.id)}>
                    📧 Send Invoice
                  </button>
                  <button className="payment-btn" onClick={() => setPaymentModal({ open: true, invoiceId: selectedInvoice.id })}>
                    Record Payment
                  </button>
                  <button className="delete-btn" onClick={() => handleDeleteInvoice(selectedInvoice.id)}>
                    Delete
                  </button>
                </div>
              )}
              {['sent', 'partial', 'overdue', 'paid'].includes(selectedInvoice.status) && (
                <div className="status-actions">
                  <button className="resend-btn" onClick={() => handleSendInvoice(selectedInvoice.id)}>
                    📧 Resend Invoice
                  </button>
                  {selectedInvoice.status !== 'paid' && (
                    <button className="payment-btn" onClick={() => setPaymentModal({ open: true, invoiceId: selectedInvoice.id })}>
                      Record Payment
                    </button>
                  )}
                  <button className="delete-btn" onClick={() => handleDeleteInvoice(selectedInvoice.id)}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="customer-info">
            <h4>Bill To:</h4>
            <p><strong>{selectedInvoice.first_name} {selectedInvoice.last_name}</strong></p>
            {selectedInvoice.organization && <p>{selectedInvoice.organization}</p>}
            {selectedInvoice.email && <p>{selectedInvoice.email}</p>}
            {selectedInvoice.phone && <p>{selectedInvoice.phone}</p>}
            {selectedInvoice.address && <p>{selectedInvoice.address}</p>}
          </div>

          <table className="invoice-items-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Qty (hrs)</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {selectedInvoice.items?.map(item => (
                <React.Fragment key={item.id}>
                  <tr>
                    <td>{formatDate(item.booking_date)}</td>
                    <td>{item.description}</td>
                    <td>{item.quantity}</td>
                    <td>{formatCurrency(item.unit_price)}/hr</td>
                    <td>{formatCurrency(item.line_total)}</td>
                  </tr>
                  <tr className="line-note-row">
                    <td colSpan="5">
                      {editingLineNote === item.id ? (
                        <div className="line-note-edit">
                          <textarea
                            value={tempLineNote}
                            onChange={e => setTempLineNote(e.target.value)}
                            placeholder="Add a note for this line item (visible to customer)..."
                            rows={2}
                          />
                          <div className="line-note-actions">
                            <button type="button" className="save-btn-small" onClick={() => handleSaveLineNote(item.id)}>Save</button>
                            <button type="button" className="cancel-btn-small" onClick={() => { setEditingLineNote(null); setTempLineNote(''); }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="line-note-display">
                          {item.line_note ? (
                            <p className="line-note-text">{item.line_note}</p>
                          ) : null}
                          <button 
                            className="edit-note-btn"
                            onClick={() => { setEditingLineNote(item.id); setTempLineNote(item.line_note || ''); }}
                          >
                            {item.line_note ? 'Edit note' : '+ Add note'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4" className="text-right">Subtotal:</td>
                <td>{formatCurrency(selectedInvoice.subtotal)}</td>
              </tr>
              {parseFloat(selectedInvoice.tax_rate) > 0 && (
                <tr>
                  <td colSpan="4" className="text-right">
                    Tax ({(parseFloat(selectedInvoice.tax_rate) * 100).toFixed(2)}%):
                  </td>
                  <td>{formatCurrency(selectedInvoice.tax_amount)}</td>
                </tr>
              )}
              <tr className="total-row">
                <td colSpan="4" className="text-right"><strong>Total:</strong></td>
                <td><strong>{formatCurrency(selectedInvoice.total)}</strong></td>
              </tr>
              <tr>
                <td colSpan="4" className="text-right">Amount Paid:</td>
                <td>{formatCurrency(selectedInvoice.amount_paid)}</td>
              </tr>
              <tr className="balance-row">
                <td colSpan="4" className="text-right"><strong>Balance Due:</strong></td>
                <td><strong>{formatCurrency(selectedInvoice.balance_due)}</strong></td>
              </tr>
            </tfoot>
          </table>

          {/* Customer Notes (visible on invoice) */}
          <div className="invoice-notes">
            <h4>Notes for Customer:</h4>
            {editingCustomerNotes ? (
              <div className="notes-edit">
                <textarea
                  value={tempCustomerNotes}
                  onChange={e => setTempCustomerNotes(e.target.value)}
                  placeholder="Add notes visible to the customer on the invoice..."
                  rows={3}
                />
                <div className="notes-actions">
                  <button className="save-btn-small" onClick={handleSaveCustomerNotes}>Save</button>
                  <button className="cancel-btn-small" onClick={() => { setEditingCustomerNotes(false); setTempCustomerNotes(''); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="notes-display">
                {selectedInvoice.notes ? (
                  <p>{selectedInvoice.notes}</p>
                ) : (
                  <p className="no-notes">No customer notes</p>
                )}
                <button 
                  className="edit-note-btn"
                  onClick={() => { setEditingCustomerNotes(true); setTempCustomerNotes(selectedInvoice.notes || ''); }}
                >
                  {selectedInvoice.notes ? 'Edit' : '+ Add notes'}
                </button>
              </div>
            )}
          </div>

          {/* Admin Notes (internal only) */}
          <div className="admin-notes-section">
            <h4>🔒 Admin Notes (Internal Only):</h4>
            {editingAdminNotes ? (
              <div className="notes-edit">
                <textarea
                  value={tempAdminNotes}
                  onChange={e => setTempAdminNotes(e.target.value)}
                  placeholder="Add internal notes (not visible to customer)..."
                  rows={3}
                />
                <div className="notes-actions">
                  <button className="save-btn-small" onClick={handleSaveAdminNotes}>Save</button>
                  <button className="cancel-btn-small" onClick={() => { setEditingAdminNotes(false); setTempAdminNotes(''); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="notes-display">
                {selectedInvoice.admin_notes ? (
                  <p>{selectedInvoice.admin_notes}</p>
                ) : (
                  <p className="no-notes">No admin notes</p>
                )}
                <button 
                  className="edit-note-btn"
                  onClick={() => { setEditingAdminNotes(true); setTempAdminNotes(selectedInvoice.admin_notes || ''); }}
                >
                  {selectedInvoice.admin_notes ? 'Edit' : '+ Add notes'}
                </button>
              </div>
            )}
          </div>

          {selectedInvoice.payments?.length > 0 && (
            <div className="payments-section">
              <h4>Payment History</h4>
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th>Recorded By</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.payments.map(p => (
                    <tr key={p.id}>
                      <td>{formatDate(p.payment_date)}</td>
                      <td>{formatCurrency(p.amount)}</td>
                      <td>{p.payment_method || '-'}</td>
                      <td>{p.reference_number || '-'}</td>
                      <td>
                        {p.recorded_by_first_name && p.recorded_by_last_name
                          ? `${p.recorded_by_first_name} ${p.recorded_by_last_name}`
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Payment Terms View */}
      {view === 'payment-terms' && (
        <div className="payment-terms-section">
          <h3>{editingTerm ? 'Edit Payment Term' : 'Create Payment Term'}</h3>
          <p className="settings-description">
            Payment terms define deposit and installment schedules for bookings above a certain amount.
            For example: 10% deposit due immediately, 50% due 60 days before first booking, balance due 30 days before.
          </p>
          
          <div className="term-form">
            <div className="form-row">
              <label>
                Name *
                <input
                  type="text"
                  value={termForm.name}
                  onChange={e => setTermForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Winter League Terms"
                />
              </label>
              <label>
                Minimum Amount ($)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={termForm.min_amount}
                  onChange={e => setTermForm(prev => ({ ...prev, min_amount: e.target.value }))}
                  placeholder="0 = applies to all"
                />
              </label>
            </div>
            
            <label>
              Description
              <textarea
                value={termForm.description}
                onChange={e => setTermForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                placeholder="Internal description of these terms..."
              />
            </label>
            
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={termForm.is_default}
                onChange={e => setTermForm(prev => ({ ...prev, is_default: e.target.checked }))}
              />
              Set as default payment terms
            </label>
            
            <div className="installments-header">
              <h4>
                Installments
                <span className="hint">(must total 100%)</span>
              </h4>
            </div>
            
            {termForm.installments.map((inst, index) => (
              <div key={index} className="installment-row">
                <label>
                  Percent
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="percent-input"
                    value={inst.percentage}
                    onChange={e => updateInstallment(index, 'percentage', parseFloat(e.target.value) || 0)}
                  />
                </label>
                
                <label>
                  Due
                  <select
                    value={inst.due_type}
                    onChange={e => updateInstallment(index, 'due_type', e.target.value)}
                  >
                    <option value="upon_receipt">Upon Receipt</option>
                    <option value="days_before">Days Before First Booking</option>
                    <option value="specific_date">Specific Date</option>
                    <option value="net_days">Net Days from Invoice</option>
                  </select>
                </label>
                
                {(inst.due_type === 'days_before' || inst.due_type === 'net_days') && (
                  <label>
                    Days
                    <input
                      type="number"
                      min="0"
                      className="days-input"
                      value={inst.days_before || ''}
                      onChange={e => updateInstallment(index, 'days_before', parseInt(e.target.value) || 0)}
                    />
                  </label>
                )}
                
                {inst.due_type === 'specific_date' && (
                  <label>
                    Date
                    <input
                      type="date"
                      className="date-input"
                      value={inst.due_date || ''}
                      onChange={e => updateInstallment(index, 'due_date', e.target.value)}
                    />
                  </label>
                )}
                
                <label className="refundable-label">
                  <input
                    type="checkbox"
                    checked={inst.is_refundable !== false}
                    onChange={e => updateInstallment(index, 'is_refundable', e.target.checked)}
                  />
                  Refundable
                </label>
                
                {termForm.installments.length > 1 && (
                  <button 
                    type="button" 
                    className="remove-installment-btn"
                    onClick={() => removeInstallment(index)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            
            <button 
              type="button" 
              className="add-installment-btn"
              onClick={addInstallment}
            >
              + Add Installment
            </button>
            
            <div className="term-form-actions">
              <button className="save-btn" onClick={handleSavePaymentTerm}>
                {editingTerm ? 'Update Term' : 'Create Term'}
              </button>
              {editingTerm && (
                <button className="cancel-btn" onClick={resetTermForm}>
                  Cancel Edit
                </button>
              )}
            </div>
          </div>
          
          {/* Existing Payment Terms */}
          <div className="existing-terms-section">
            <h3>Existing Payment Terms</h3>
            {paymentTerms.length === 0 ? (
              <p className="no-invoices">No payment terms configured yet.</p>
            ) : (
              <table className="terms-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Min Amount</th>
                    <th>Installments</th>
                    <th>Default</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentTerms.map(term => (
                    <tr key={term.id}>
                      <td>
                        <div className="term-name">{term.name}</div>
                        {term.description && <div className="term-description">{term.description}</div>}
                      </td>
                      <td>{term.min_amount > 0 ? formatCurrency(term.min_amount) : 'Any'}</td>
                      <td>
                        {(term.installments || []).map((inst, i) => (
                          <div key={i} className="installment-detail">
                            {inst.percentage}% - {
                              inst.due_type === 'upon_receipt' ? 'Upon Receipt' :
                              inst.due_type === 'days_before' ? `${inst.days_before} days before` :
                              inst.due_type === 'specific_date' ? `Due ${inst.due_date}` :
                              `Net ${inst.days_before} days`
                            }
                            {inst.is_refundable === false && <span className="non-refundable"> (non-refundable)</span>}
                          </div>
                        ))}
                      </td>
                      <td>{term.is_default ? <span className="default-badge">✓ Default</span> : ''}</td>
                      <td>
                        <div className="actions">
                          <button className="edit-btn" onClick={() => handleEditTerm(term)}>
                            Edit
                          </button>
                          <button className="delete-btn" onClick={() => handleDeleteTerm(term.id)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Record Payment</h3>
            <div className="form-row">
              <label>
                Amount *
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                />
              </label>
              <label>
                Payment Method
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                  <option value="">-- Select --</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="card">Credit/Debit Card</option>
                  <option value="transfer">Bank Transfer</option>
                  <option value="other">Other</option>
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>
                Reference #
                <input
                  type="text"
                  value={paymentRef}
                  onChange={e => setPaymentRef(e.target.value)}
                  placeholder="Check #, Transaction ID, etc."
                />
              </label>
              <label>
                Payment Date
                <input
                  type="date"
                  value={paymentDate}
                  onChange={e => setPaymentDate(e.target.value)}
                />
              </label>
            </div>
            <label>
              Notes
              <textarea
                value={paymentNotes}
                onChange={e => setPaymentNotes(e.target.value)}
                rows={2}
              />
            </label>
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setPaymentModal({ open: false, invoiceId: null })}>
                Cancel
              </button>
              <button className="modal-confirm" onClick={handleRecordPayment}>
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Payment Terms Modal */}
      {applyTermsModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Apply Payment Terms</h3>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              Select payment terms to create an installment schedule for this invoice.
            </p>
            
            <label>
              Payment Terms
              <select 
                value={selectedTermId} 
                onChange={e => setSelectedTermId(e.target.value)}
                style={{ width: '100%', padding: '8px', marginTop: '4px' }}
              >
                <option value="">-- Simple Due Date (No Terms) --</option>
                {paymentTerms.map(term => (
                  <option key={term.id} value={term.id}>
                    {term.name} {term.min_amount > 0 ? `(min ${formatCurrency(term.min_amount)})` : ''}
                  </option>
                ))}
              </select>
            </label>
            
            {selectedTermId && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '6px' }}>
                <strong>Installment Preview:</strong>
                {paymentTerms.find(t => t.id === parseInt(selectedTermId))?.installments?.map((inst, i) => (
                  <div key={i} style={{ marginTop: '8px', fontSize: '0.9rem' }}>
                    • {inst.percentage}% - {
                      inst.due_type === 'upon_receipt' ? 'Upon Receipt' :
                      inst.due_type === 'days_before' ? `${inst.days_before} days before first booking` :
                      inst.due_type === 'specific_date' ? `Due ${inst.due_date}` :
                      `Net ${inst.days_before} days`
                    }
                    {inst.is_refundable === false && <span style={{ color: '#dc2626' }}> (non-refundable)</span>}
                  </div>
                ))}
              </div>
            )}
            
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => { setApplyTermsModal(false); setSelectedTermId(''); }}>
                Cancel
              </button>
              <button className="modal-confirm" onClick={handleApplyTerms}>
                Apply Terms
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminInvoices;
